import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Divider, HStack, IconButton, Image, Spinner, Stack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { colors } from "../../../styles";
import Template from "../../../components/Layout/Template";
import AddModal from "../Feed/AddModal";
import ChatListItem from "../../../components/Chat/ListItem/ChatListItem";
import MyIcon from "../../../components/Icon/MyIcon";
import useMe from "../../../hooks/useMe";
import { Fragment, useEffect, useRef, useState } from "react";
import LoadingThreeDotsJumping from "../../../components/Indicator/JumpingDot";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { IChat, IChatList, IMyProfile } from "../../../types";
import { getChats, getMyProfile } from "../../../api";
import ADDivider from "../../../components/Ad/ADDivider";
import FakeInput from "../../../components/Chat/FakeInput";
import { useAuthStore } from "../../../store";
import FilterModal, { CommentSortFilter } from "../FilterModal";
import LoadingRipple from "../../../components/Indicator/Ripple";
import { ChatDeleteLoadingProvider } from "../../../components/Chat/ChatDeleteLoading";
import { INTENTIONAL_LOADING_MS } from "../../../hooks/useMinDelayLoading";

type ChatDisplayItem =
    | { type: "chat"; chat: IChat; key: string }
    | { type: "ad"; key: string; slotIndex: number };

export default function Feed() {

    /* query */

    const queryClient = useQueryClient();

    const { data: myProfileData } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: () => getMyProfile(),
    });

    const myNickname = myProfileData?.nickname;

    /* state */

    const [isMyTypeOnly, setIsMyTypeOnly] = useState<boolean>(false);

    const [filter, setFilter] = useState<CommentSortFilter>("latest");
    const filterLabel = filter === "latest" ? "최신순" : "인기순";

    const { isOpen: isFilterModalOpen, onOpen: onFilterModalOpen, onClose: onFilterModalClose } = useDisclosure();

    /* useInfiniteQuery  */

    const size = 20;

    const { 
        data: chatListData, 
        isLoading: isChatListLoading, 
        isFetching: isChatListFetching,
        fetchNextPage: fetchNextChatList, 
        hasNextPage: hasNextChatListPage, 
        isFetchingNextPage: isFetchingNextChatListPage, 
    } = useInfiniteQuery<IChatList>({

        // 쿼리 키 (단어 검색 전용, 필터 포함)
        queryKey: ['chats', filter, isMyTypeOnly],

        // 쿼리 함수 - pageParam은 useInfiniteQuery의 파라미터로 전달되는 값 중 페이지 번호를 의미
        queryFn: ({ pageParam }) => {

            // pageParam은 unknown 타입이므로 명시적으로 number 타입으로 변환
            const page = pageParam as number;
            
            return getChats({ 
                page, 
                size, 
                filter,
                isMyTypeOnly,
            });
        },

        // 초기 페이지 파라미터 : 1 또는 0 사용
        initialPageParam: 1,

        // 쿼리 활성화 조건: 검색어가 확정된 경우에만
        enabled: true,

        // 다음 페이지 파라미터 결정
        getNextPageParam: (lastPageData: IChatList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        },
    });

    console.log(chatListData);

    const renderChatList = () => {

        if (isChatListLoading) {
            return (
                <VStack width={"100%"} justifyContent={"center"} alignItems={"center"} paddingY={10}>
                    <Spinner color={colors.blue} />
                </VStack>
            );
        }

        const pages = chatListData?.pages ?? [];
        const items: ChatDisplayItem[] = [];
        let chatCount = 0;
        let adSlotIndex = 0;

        pages.forEach((page) => {
            page.chats.forEach((chat) => {
                if (chatCount !== 0 && chatCount % 8 === 0) {
                    items.push({ type: "ad", key: `ad-${adSlotIndex}`, slotIndex: adSlotIndex });
                    adSlotIndex += 1;
                }
                items.push({ type: "chat", chat, key: chat.id });
                chatCount += 1;
            });
        });

        return items.map((item, index) => {

            const isFirst = index === 0;
            const isLast = index === items.length - 1;
            
            if (item.type === "ad") {
                return (
                    <ADDivider key={item.key} slotIndex={item.slotIndex} />
                );
            }

            const { chat } = item;

            return (
                <ChatListItem
                    key={item.key}
                    id={chat.id}
                    slug={chat.slug}
                    profileId={chat.profile.id}
                    profileImageUrl={chat.profile.avatar}
                    userType={chat.profile.user.type}
                    authorType={chat.authorType}
                    nickname={chat.profile.nickname ?? ""}
                    content={chat.content}
                    imageUrls={chat.imageUrls}
                    isMyTypeOnly={chat.isMyTypeOnly}
                    likeCount={chat.likeCount}
                    commentCount={chat.commentCount}
                    saveCount={chat.saveCount}
                    isMine={chat.isMine}
                    isLiked={chat.isLiked}
                    isSaved={chat.isSaved}
                    isReported={chat.isReported}
                    isBlocking={chat.isBlocking}
                    hiddenAt={chat.hiddenAt}
                    createdAt={chat.createdAt}
                    isFirst={isFirst}
                    isLast={isLast}
                />
            );
        });
    };

    /* 모달 상태 관리 */

    const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();

    /* 게시 로딩 인디케이터
     * 인디케이터 노출 시간 = max(INTENTIONAL_LOADING_MS, 실제 API 응답 시간)
     * - 게시 버튼 클릭 시 isPostLoading = true
     * - 최소 지연 완료 && API 응답 완료, 두 조건이 모두 충족돼야 숨김
     */

    const [isPostLoading, setIsPostLoading] = useState(false);
    const minDelayDoneRef = useRef(false);
    const apiDoneRef = useRef(false);
    const minDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tryHideIndicator = () => {
        if (minDelayDoneRef.current && apiDoneRef.current) {
            // 두 조건 모두 충족 → 피드 갱신 후 인디케이터 숨김
            queryClient.invalidateQueries({ queryKey: ["chats"] });
            setIsPostLoading(false);
            minDelayDoneRef.current = false;
            apiDoneRef.current = false;
        }
    };

    const handlePostStart = () => {
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
        setIsPostLoading(true);

        if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        minDelayTimerRef.current = setTimeout(() => {
            minDelayDoneRef.current = true;
            tryHideIndicator();
        }, INTENTIONAL_LOADING_MS);
    };

    const handlePostDone = () => {
        apiDoneRef.current = true;
        tryHideIndicator();
    };

    const handlePostError = () => {
        if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        setIsPostLoading(false);
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
    };

    useEffect(() => {
        return () => {
            if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        };
    }, []);

    /* render */

    return (
        <ChatDeleteLoadingProvider
            onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ["chats"] });
            }}
        >
        <Template>

            {/* 모두 / 내 유형만 */}

            <HStack
                width={"100%"}
                justifyContent={"center"}
                alignItems={"center"}
                height={12}
                gap={10}
                marginBottom={{
                    base: 4,
                    sm: 4,
                    md: 4,
                    lg: 8,
                    xl: 8,
                }}
            >
                <HStack
                    as={"button"}
                    type={"button"}
                    gap={2}
                    alignItems={"center"}
                    onClick={() => setIsMyTypeOnly(false)}
                >
                    <Text
                        fontSize={!isMyTypeOnly ? 18 : 16}
                        fontWeight={!isMyTypeOnly ? 700 : 400}
                        color={!isMyTypeOnly ? colors.blue : colors.g3}
                        transition={"all 0.5s ease"}
                    >
                        모두
                    </Text>
                </HStack>
                <HStack
                    as={"button"}
                    type={"button"}
                    gap={1}
                    alignItems={"center"}
                    onClick={() => setIsMyTypeOnly(true)}
                >
                    <Text
                        fontSize={isMyTypeOnly ? 18 : 16}
                        fontWeight={isMyTypeOnly ? 700 : 400}
                        color={isMyTypeOnly ? colors.blue : colors.g3}
                        transition={"all 0.5s ease"}
                    >
                        내 유형만
                    </Text>
                </HStack>
            </HStack>

            {/* 가짜 입력 */}

            <VStack 
                width={"100%"} 
                justifyContent={"center"} 
                alignItems={"center"}
            >      
                <FakeInput
                    avatar={myProfileData?.avatar ?? "/png/profile.png"}
                    nickname={myProfileData?.nickname ?? ""}
                    description={"어떤 소식이 있나요?"}
                    onClick={onAddModalOpen}
                />
            </VStack>

            <HStack 
                width={"100%"} 
                justifyContent={"center"} 
                alignItems={"center"} 
                marginY={5}
            >
                <HStack
                    width={{
                        base:"100%",
                        sm:"100%",
                        md:"100%",
                        lg:"80%",
                        xl:"80%",
                    }}  
                    justifyContent={"space-between"}
                    alignItems={"center"}
                >
                    <Text fontSize={12} fontWeight={400} color={colors.g3}>
                        24시간 내 작성된 글만 게시됩니다.
                    </Text>
                    <HStack 
                        as="button"
                        type="button"
                        justifyContent={"flex-end"} 
                        alignItems={"center"}
                        gap={2}
                        onClick={onFilterModalOpen}
                    >
                        <MyIcon src={"/svg/filter-2.svg"} alt={"필터"} width={16} height={16} />
                        <Text fontSize={14} fontWeight={500} color={colors.black}>{filterLabel}</Text>
                    </HStack>
                </HStack>
            </HStack>

            {/* 채팅 목록 */}

            <VStack 
                width={"100%"} 
                justifyContent={"flex-start"} 
                alignItems={"center"}
                spacing={5}
            >
                {renderChatList()}

                {hasNextChatListPage && (
                    <Button variant={"ghost"} onClick={() => fetchNextChatList()} isLoading={isFetchingNextChatListPage} marginTop={5}>
                        <MyIcon src={"/svg/arrow-down.svg"} alt={"더보기"} width={20} height={20} />
                    </Button>
                )}

            </VStack>

            {/* 채팅 추가 버튼 및 프로필 버튼 */}

            <VStack
                position={"fixed"}
                right={{ base: 4, sm: 8, md: 12 }}
                bottom={{ base: 4, sm: 8, md: 12 }}
                spacing={{ base: 3, sm: 4, md: 4 }}
            >
                <IconButton
                    as={Link}
                    to={`/chat/profile/my`}
                    aria-label={"프로필"}
                    icon={<Image src={myProfileData?.avatar ?? "/png/profile.png"} alt={"프로필"} boxSize={"full"} objectFit={"cover"} />}
                    boxSize={{ base: 16, sm: 20, md: 24 }}
                    minWidth={{ base: 16, sm: 20, md: 24 }}
                    borderRadius={"full"}
                    backgroundColor={colors.white}
                    boxShadow={"lg"}
                    overflow={"hidden"}
                    padding={0}
                    _hover={{ backgroundColor: colors.white }}
                    _active={{ backgroundColor: colors.white }}
                />
                <IconButton
                    aria-label={"채팅 추가"}
                    icon={<AddIcon boxSize={{ base: 6, sm: 7, md: 8 }} />}
                    boxSize={{ base: 16, sm: 20, md: 24 }}
                    minWidth={{ base: 16, sm: 20, md: 24 }}
                    borderRadius={"full"}
                    backgroundColor={colors.blue}
                    color={colors.white}
                    boxShadow={"lg"}
                    _hover={{ backgroundColor: colors.blue }}
                    _active={{ backgroundColor: colors.blue }}
                    onClick={onAddModalOpen}
                />
            </VStack>

            {isPostLoading && (
                <Box
                    position="fixed"
                    inset={0}
                    zIndex={9999}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="none"
                >
                    {/* <LoadingThreeDotsJumping /> */}
                    <LoadingRipple />
                </Box>
            )}

            <AddModal isOpen={isAddModalOpen} onClose={onAddModalClose} onPostStart={handlePostStart} onPostDone={handlePostDone} onPostError={handlePostError} />
            <FilterModal isOpen={isFilterModalOpen} onClose={onFilterModalClose} filter={filter} onChangeFilter={setFilter} />
        </Template>
        </ChatDeleteLoadingProvider>
    );
}
